import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Calendar,
  Euro,
  Building,
  Crown,
  Coffee,
  CheckCircle,
  Star,
  ArrowRight,
} from "lucide-react";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const BrusselsPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: Building,
      title: t("1550", "Grand Place"),
      description: t(
        "1551",
        "UNESCO World Heritage site with stunning architecture"
      ),
    },
    {
      icon: Crown,
      title: t("1552", "Royal Quarter"),
      description: t("1553", "Explore royal palaces and European institutions"),
    },
    {
      icon: Coffee,
      title: t("1524", "Belgian Cuisine"),
      description: t("1554", "Famous waffles, chocolates, and beer culture"),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t("1555", "Capture the essence of Europe's capital city"),
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1556", "Grand Place guided tour"),
    t("1557", "European Quarter visits"),
    t("1558", "Belgian culinary experiences"),
    t("1559", "Royal Palace area exploration"),
    t("1489", "Photography opportunities"),
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1491", "Full Day (8-10 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1560", "€160 per person"),
      icon: Euro,
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Brussel.webp"
            alt={t("1561", "Brussels - Capital of Europe")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Building className="h-4 w-4" />
              <span>{t("1499", "Premium Tour Experience")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1500", "Discover")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1562", "Brussels")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1563",
                "Capital of Europe with rich history, stunning architecture, and world-renowned culinary delights"
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1564", "4.8/5 (98 reviews)")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  {t("1565", "Explore the Heart of")}{" "}
                  <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                    {t("1566", "Europe")}
                  </span>
                </h2>

                <p className="text-lg text-gray-100 leading-relaxed">
                  {t(
                    "1567",
                    "Discover Brussels, the captivating capital of Belgium and the administrative heart of the European Union, where medieval charm meets modern diplomacy."
                  )}
                </p>

                <p className="text-gray-200 leading-relaxed">
                  {t(
                    "1568",
                    "From the stunning Grand Place with its opulent guildhalls to the impressive European Quarter, Brussels offers a unique blend of history, politics, and culinary excellence. Experience world-famous chocolates, authentic waffles, and Belgium's legendary beer culture."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  {t("1508", "Tour Highlights")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {tourHighlights.map((highlight) => (
                    <motion.div
                      key={highlight.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                    >
                      <highlight.icon className="h-8 w-8 text-primary mb-3" />
                      <div className="font-semibold text-white text-sm mb-2">
                        {highlight.title}
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed">
                        {highlight.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4 h-[600px]">
                <div className="space-y-4">
                  <div className="relative overflow-hidden rounded-2xl shadow-xl">
                    <img
                      src="/images/tours/Brussel.webp"
                      alt={t("1569", "Brussels Grand Place")}
                      className="w-full h-60 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="relative overflow-hidden rounded-2xl shadow-xl mt-8">
                    <img
                      src="/images/tours/Brussel.webp"
                      alt={t("1570", "European Quarter")}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-2xl shadow-xl col-span-2">
                  <img
                    src="/images/tours/Brussel.webp"
                    alt={t("1571", "Brussels Architecture")}
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details & Booking */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {t(
                "1572",
                "Everything you need to know about your Brussels experience"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8">
                {t("1514", "Tour Details")}
              </h3>

              <div className="space-y-6">
                {tourDetails.map((detail) => (
                  <motion.div
                    key={detail.label}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                  >
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <detail.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {detail.label}
                      </div>
                      <div className="text-gray-300">{detail.value}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8">
                {t("1573", "What's Included")}
              </h3>

              <div className="space-y-4">
                {tourFeatures.map((item) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>

            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("1574", "Ready to Discover Brussels?")}
              </h2>
              <p className="text-xl mb-8 opacity-90">
                {t("1575", "Book your European capital adventure today")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {t("1518", "Book Now")}
                </button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1519", "Ask Questions")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
